/**
 * Continue Listening order helpers — match mobile (Flutter) behaviour:
 * - dateAdded is sticky (set once when title first enters played_content)
 * - dateUpdated tracks progress and is secondary sort only
 * - Sort: dateAdded DESC, then dateUpdated DESC
 * - Never use CMS Title.createdAt/updatedAt for ordering
 */

export type PlayedOrderTimestamps = {
  dateAdded?: string
  dateUpdated?: string
  created_at?: string
  last_updated_at?: string
  queuePosition?: number
  position?: number
}

function toIso(value: unknown, fallback: string): string {
  if (typeof value === 'string' && value) {
    const t = new Date(value).getTime()
    return Number.isNaN(t) ? fallback : new Date(t).toISOString()
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString()
  }
  return fallback
}

/** Normalize title ids so `audio-123` / `epub-123` / `123` share one key */
export function normalizePlayedTitleId(raw: unknown): string {
  const id = String(raw ?? '').trim()
  if (!id || id === 'undefined' || id === 'null') return ''
  return id.replace(/^(audio|epub)-/i, '')
}

/**
 * Resolve sticky dateAdded from an existing played item (or now for new items).
 * Prefers dateAdded, then created_at, then last_updated_at (server / fresh-device path).
 */
export function resolveStickyDateAdded(
  existing: PlayedOrderTimestamps | null | undefined,
  nowIso: string = new Date().toISOString()
): string {
  if (!existing) return nowIso
  if (existing.dateAdded) return toIso(existing.dateAdded, nowIso)
  if (existing.created_at) return toIso(existing.created_at, nowIso)
  if (existing.last_updated_at) return toIso(existing.last_updated_at, nowIso)
  if (existing.dateUpdated) return toIso(existing.dateUpdated, nowIso)
  return nowIso
}

/**
 * Primary Continue Listening sort timestamp.
 * Matches mobile: dateAdded, else created_at, else last progress time (fresh sync).
 */
export function resolvePlayedOrderPrimary(item: PlayedOrderTimestamps): number {
  return playedDateMs(
    item.dateAdded || item.created_at || item.last_updated_at || item.dateUpdated
  )
}

/**
 * Secondary sort timestamp (progress), never CMS Title.updatedAt.
 */
export function resolvePlayedOrderSecondary(item: PlayedOrderTimestamps): number {
  return playedDateMs(item.dateUpdated || item.last_updated_at)
}

/**
 * Whether position changed enough to bump dateUpdated (mobile uses > 10 seconds).
 */
export function shouldBumpDateUpdated(
  existing: PlayedOrderTimestamps | null | undefined,
  newPosition: number | undefined,
  thresholdSeconds = 10
): boolean {
  if (!existing?.dateUpdated && !existing?.last_updated_at) return true
  if (newPosition === undefined || newPosition === null) return true
  const prev =
    typeof existing.queuePosition === 'number'
      ? existing.queuePosition
      : typeof existing.position === 'number'
        ? existing.position
        : 0
  return Math.abs(newPosition - prev) > thresholdSeconds
}

/**
 * Apply sticky dateAdded + conditional dateUpdated onto a played item payload.
 * Also keeps created_at aligned with dateAdded for legacy sort/sync paths.
 */
export function applyStickyPlayedTimestamps<T extends PlayedOrderTimestamps>(
  existing: T | null | undefined,
  updates: T,
  options?: { nowIso?: string; newPosition?: number }
): T {
  const nowIso = options?.nowIso ?? new Date().toISOString()
  const dateAdded = resolveStickyDateAdded(existing, nowIso)
  const bump = shouldBumpDateUpdated(
    existing,
    options?.newPosition ?? updates.queuePosition ?? updates.position
  )
  const dateUpdated = bump
    ? nowIso
    : toIso(
        existing?.dateUpdated || existing?.last_updated_at || nowIso,
        nowIso
      )

  return {
    ...updates,
    dateAdded,
    dateUpdated,
    created_at: dateAdded,
    last_updated_at: dateUpdated,
  }
}

/** Parse a date field to epoch ms; invalid/missing → 0 */
export function playedDateMs(value: unknown): number {
  if (!value) return 0
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const t = new Date(value as string).getTime()
  return Number.isNaN(t) ? 0 : t
}

/**
 * Resolve audio resume seconds from either web (`queuePosition`) or API/mobile (`position`).
 * Returns null when neither field is present.
 */
export function resolvePlayedAudioPosition(
  item: PlayedOrderTimestamps | null | undefined
): number | null {
  if (!item) return null
  const raw =
    item.queuePosition !== undefined && item.queuePosition !== null
      ? item.queuePosition
      : item.position !== undefined && item.position !== null
        ? item.position
        : null
  if (raw === null) return null
  const value = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(value) ? value : null
}

/** True when there is enough progress to show Continue (not just an empty played stub). */
export function hasResumableAudioProgress(
  item: PlayedOrderTimestamps | null | undefined,
  minSeconds = 1
): boolean {
  const position = resolvePlayedAudioPosition(item)
  return position !== null && position >= minSeconds
}

/**
 * Pick the fresher audio progress between local and server records.
 * Avoids full-sync pulling an older server position over a newer local pause.
 */
export function pickFresherPlayedAudioProgress<T extends PlayedOrderTimestamps>(
  local: T | null | undefined,
  server: T | null | undefined
): { position: number; queuePosition: number; source: 'local' | 'server' | 'none' } {
  const localPos = resolvePlayedAudioPosition(local) ?? 0
  const serverPos = resolvePlayedAudioPosition(server) ?? 0
  const localTs = playedDateMs(local?.last_updated_at || local?.dateUpdated)
  const serverTs = playedDateMs(server?.last_updated_at || server?.dateUpdated)

  if (!local && !server) {
    return { position: 0, queuePosition: 0, source: 'none' }
  }
  if (!local) {
    return { position: serverPos, queuePosition: serverPos, source: 'server' }
  }
  if (!server) {
    return { position: localPos, queuePosition: localPos, source: 'local' }
  }
  if (localTs > serverTs) {
    return { position: localPos, queuePosition: localPos, source: 'local' }
  }
  if (serverTs > localTs) {
    return { position: serverPos, queuePosition: serverPos, source: 'server' }
  }
  // Same/unknown timestamps: never regress (stale server 8s vs local 30s)
  const position = Math.max(localPos, serverPos)
  return {
    position,
    queuePosition: position,
    source: localPos >= serverPos ? 'local' : 'server',
  }
}

export type PlayedAudioProgressFields = {
  queuePosition?: number
  position?: number
  queueIndex?: number | string
  chapter_id?: number | string
}

/**
 * Keep web (`queuePosition`/`queueIndex`) and API (`position`/`chapter_id`) fields in sync
 * so resume works after local saves and after sync pull.
 */
export function normalizePlayedAudioProgressFields<T extends PlayedAudioProgressFields>(
  item: T
): T {
  const position = resolvePlayedAudioPosition(item)
  const chapterRaw =
    item.chapter_id !== undefined && item.chapter_id !== null
      ? item.chapter_id
      : item.queueIndex !== undefined && item.queueIndex !== null
        ? item.queueIndex
        : undefined

  const next: T = { ...item }
  if (position !== null) {
    next.queuePosition = position
    next.position = position
  }
  if (chapterRaw !== undefined) {
    next.queueIndex = chapterRaw
    const chapterNum =
      typeof chapterRaw === 'number' ? chapterRaw : parseInt(String(chapterRaw), 10)
    if (!Number.isNaN(chapterNum)) {
      next.chapter_id = chapterNum
    }
  }
  return next
}

/**
 * Compare two played items for Continue Listening order (newest first).
 * Primary: dateAdded / created_at / last_updated_at.
 * Secondary: dateUpdated / last_updated_at.
 * Does not use CMS Title dates.
 */
export function comparePlayedContentOrder(
  a: PlayedOrderTimestamps,
  b: PlayedOrderTimestamps
): number {
  const dateAddedDiff = resolvePlayedOrderPrimary(b) - resolvePlayedOrderPrimary(a)
  // Match mobile: only use dateUpdated when dateAdded is essentially equal
  if (Math.abs(dateAddedDiff) > 1) return dateAddedDiff
  return resolvePlayedOrderSecondary(b) - resolvePlayedOrderSecondary(a)
}
